import { Link } from 'react-router-dom';

const guides = [
  {
    slug: 'where-to-buy-used-cars-near-astoria-oregon',
    category: 'Local Buyer Guide',
    readTime: '8 min read',
    title: 'Where Can I Buy a Used Car Near Astoria, Oregon?',
    excerpt:
      'An honest breakdown of every option available to Clatsop County buyers — from local dealerships to auto brokers — and how to avoid overpaying in a market with limited competition.',
    icon: 'ri-map-pin-2-line',
    accent: 'teal',
    tags: ['Astoria', 'Local Options', 'Broker vs Dealer'],
  },
  {
    slug: 'most-reliable-cars-oregon-coast',
    category: 'Local Buyer Guide',
    readTime: '9 min read',
    title: 'Most Reliable Cars for the Oregon Coast',
    excerpt:
      'The Oregon Coast\'s wet climate, salt air, and winding roads demand more from a vehicle. Here\'s what actually holds up — and what to avoid — based on years of buying and selling in Clatsop County.',
    icon: 'ri-shield-check-line',
    accent: 'emerald',
    tags: ['AWD', 'Salt Air', 'Reliability'],
  },
  {
    slug: 'is-it-cheaper-to-buy-car-in-warrenton-than-portland',
    category: 'Local Buyer Guide',
    readTime: '7 min read',
    title: 'Is It Cheaper to Buy a Car in Warrenton Than Portland?',
    excerpt:
      'Portland has more dealerships and more competition. So why do some buyers end up paying less in Warrenton? The answer has everything to do with how you buy — not where.',
    icon: 'ri-price-tag-3-line',
    accent: 'amber',
    tags: ['Pricing', 'Warrenton vs Portland', 'Savings'],
  },
];

const accentMap: Record<string, { bg: string; text: string; border: string; badge: string; badgeText: string }> = {
  teal: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-200 hover:border-teal-400',
    badge: 'bg-teal-100',
    badgeText: 'text-teal-700',
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200 hover:border-emerald-400',
    badge: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200 hover:border-amber-400',
    badge: 'bg-amber-100',
    badgeText: 'text-amber-700',
  },
};

export default function AstoriaLocalGuides() {
  return (
    <section className="py-16 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full mb-4 uppercase tracking-wide">
            Astoria Buyer Resources
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Local Guides Written for Astoria Buyers
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            These articles answer the exact questions Clatsop County buyers ask us most. 
            Read them before you shop — they'll save you thousands.
          </p>
        </div>

        {/* Guide Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {guides.map((guide) => {
            const colors = accentMap[guide.accent];
            return (
              <Link
                key={guide.slug}
                to={`/blog/${guide.slug}`}
                className={`group flex flex-col bg-white rounded-xl border-2 ${colors.border} shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden cursor-pointer`}
              >
                {/* Card Top Bar */}
                <div className={`${colors.bg} px-6 pt-6 pb-4`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${colors.text}`}>
                      {guide.category}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {guide.readTime}
                    </span>
                  </div>
                  <div className={`w-10 h-10 ${colors.badge} rounded-lg flex items-center justify-center mb-3`}>
                    <i className={`${guide.icon} text-xl ${colors.badgeText}`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 leading-snug group-hover:text-teal-700 transition-colors">
                    {guide.title}
                  </h3>
                </div>

                {/* Card Body */}
                <div className="px-6 py-4 flex-1 flex flex-col">
                  <p className="text-sm text-gray-600 leading-relaxed flex-1">
                    {guide.excerpt}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {guide.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`text-xs px-2 py-1 rounded-full ${colors.badge} ${colors.badgeText} font-medium`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Read Link */}
                  <div className={`mt-4 flex items-center gap-1 text-sm font-semibold ${colors.text} group-hover:gap-2 transition-all`}>
                    Read the guide
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA strip */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-book-open-line text-xl text-teal-700"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Want more buyer guides?</p>
              <p className="text-gray-500 text-sm">We publish honest, local-first content for Oregon Coast buyers.</p>
            </div>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg font-semibold text-sm hover:bg-teal-700 transition whitespace-nowrap"
          >
            <i className="ri-article-line"></i>
            View All Buyer Guides
          </Link>
        </div>
      </div>
    </section>
  );
}
